import { useEffect, useRef } from "react";

// Full-screen 3D parallax emerald digital confetti — canvas-based, zero deps.
export default function Confetti({ duration = 5000, density = 160 }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let w = (canvas.width = window.innerWidth);
    let h = (canvas.height = window.innerHeight);
    const onResize = () => {
      w = canvas.width = window.innerWidth;
      h = canvas.height = window.innerHeight;
    };
    window.addEventListener("resize", onResize);

    // 3D-ish particles: z drives scale + speed (parallax)
    const shapes = ["square", "line", "digit"];
    const particles = Array.from({ length: density }, () => {
      const z = Math.random(); // 0 = far, 1 = near
      return {
        x: Math.random() * w,
        y: -Math.random() * h,
        z,
        vx: (Math.random() - 0.5) * (0.4 + z * 1.2),
        vy: 1 + z * 4 + Math.random() * 1.5,
        rot: Math.random() * Math.PI * 2,
        vr: (Math.random() - 0.5) * 0.2,
        size: 4 + z * 12,
        shape: shapes[(Math.random() * shapes.length) | 0],
        alpha: 0.4 + z * 0.6,
        digit: ["$", "0", "1", "$", "€", "0", "1"][(Math.random() * 7) | 0],
      };
    });

    const start = performance.now();
    const draw = (t) => {
      const elapsed = t - start;
      ctx.clearRect(0, 0, w, h);
      for (const p of particles) {
        p.x += p.vx;
        p.y += p.vy;
        p.rot += p.vr;
        if (p.y > h + 40) {
          p.y = -20;
          p.x = Math.random() * w;
        }
        ctx.save();
        ctx.globalAlpha = p.alpha;
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.shadowColor = "#10b981";
        ctx.shadowBlur = 6 + p.z * 10;
        ctx.fillStyle = p.z > 0.6 ? "#34d399" : "#10b981";
        ctx.strokeStyle = "#10b981";
        if (p.shape === "square") {
          ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.35);
        } else if (p.shape === "line") {
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.moveTo(-p.size / 2, 0);
          ctx.lineTo(p.size / 2, 0);
          ctx.stroke();
        } else {
          ctx.font = `bold ${p.size}px "JetBrains Mono", monospace`;
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillText(p.digit, 0, 0);
        }
        ctx.restore();
      }
      if (elapsed < duration) {
        rafRef.current = requestAnimationFrame(draw);
      } else {
        ctx.clearRect(0, 0, w, h);
      }
    };
    rafRef.current = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", onResize);
    };
  }, [duration, density]);

  return (
    <canvas
      ref={canvasRef}
      data-testid="celebration-confetti"
      className="fixed inset-0 pointer-events-none z-[60]"
      style={{ mixBlendMode: "screen" }}
    />
  );
}
